import * as React from 'react'
import { connect } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import type { RootState, Dispatch } from '../../app/store'
import Angle from '../../icons/Angle'
import type { ItemT, Coupon, CouponValidation } from '../../models/cart'
import type { StatusT } from '../../models/order'
import { getFormattedPrice } from '../../utils/formattedPrice'
import QuantityButton from '../QuantityButton'
import styles from './Cart.module.css'
import { getDiscountAmountText, getDiscountErrorMessage, getDiscountTypeText } from '../../utils/discountHelper'
import { handleOpeningHours } from '../../utils/openingHours'
import { OpeningHoursT } from '../../models/menus'
import loader from '../../images/loading.gif'
import { DELIVERY_TYPE, DeliveryType } from '../../utils/deliveryHelper'

type CartT = {
  status: StatusT,
  cartItems: Array<ItemT>,
  deliveryType?: DeliveryType
  tableNumber: string | undefined,
  totalPrice: number,
  coupon: Coupon
  couponLoading: boolean,
  couponApplied: boolean,
  currentOrderId: string | null
  fetchCart: (payload?: any) => Promise<void>,
  removeItemFromCart: (cartItemId: string) => Promise<void>
  updateItemQuantity: (payload: any) => Promise<void>
  validateCoupon: ((payload: string) => Promise<CouponValidation>)
  removeCoupon: (payload: string) => Promise<void>
  openingHours: OpeningHoursT[] | undefined
  cartId: string | null
  deliveryFee: number
  resetOrderStatus: () => void
}

function Cart ({ status, deliveryType, cartItems, totalPrice, couponLoading, fetchCart, removeItemFromCart, updateItemQuantity, tableNumber, coupon, couponApplied, validateCoupon, removeCoupon, openingHours, cartId, deliveryFee, resetOrderStatus }: CartT) {
  const navigate = useNavigate()
  const location = useLocation()
  const { storeId } = useParams<any>()
  const [cartIsOpen, setCartIsOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [discountCode, setDiscountCode] = React.useState('')
  const [discountErrorMessage, setDiscountErrorMessage] = React.useState<string>('')
  const [loadingDiscount, setLoadingDiscount] = React.useState(false)
  const cartRef = React.useRef<HTMLDivElement>(null)

  const getDeliveryTypeText = React.useCallback(() => {
    if (tableNumber) {
      return `- Bord: ${tableNumber}`
    } else if (deliveryType === DELIVERY_TYPE.EATIN) {
      return '- Äta här'
    } else if (deliveryType === DELIVERY_TYPE.TAKEAWAY) {
      return '- Ta med'
    } 
    return ''
  }, [deliveryType, tableNumber])

  React.useEffect(() => {
    getDeliveryTypeText()
  }, [deliveryType, getDeliveryTypeText])

  React.useEffect(() => {
    fetchCart()
    resetOrderStatus()
  }, [fetchCart, resetOrderStatus])

  React.useEffect(() => {
    if (!cartIsOpen) return

    function handleClickOutside(event: MouseEvent) {
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        setCartIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [cartIsOpen])

  React.useEffect(() => {
    if (couponApplied && coupon.code) {
      setDiscountCode(coupon.code)
    } else {
      setDiscountCode('')
    }
  }, [couponApplied, coupon.code])

  React.useEffect(() => {
    if (!couponApplied || !cartId) return

    if (cartItems.length === 0) {
      removeCoupon(cartId as string)
    } else if (totalPrice === coupon.totalDiscount) {
      removeCoupon(cartId as string)
      setDiscountErrorMessage(getDiscountErrorMessage('DISCOUNT_EXCEEDS_TOTAL'))
    }
  }, [cartItems.length, totalPrice, couponApplied, cartId])

  React.useEffect(() => {
    const { isOpen } = handleOpeningHours(openingHours || [])
    if (!isOpen) {
      navigate(`/butik/${storeId}`)
    }
  }, [])

  function handleCartIsOpen () {
    setCartIsOpen(prev => !prev)
  }

  function getNumberOfArticlesText () {

    const numberOfItems = cartItems.reduce((prev: number, current: ItemT) => {
      return prev + current.quantity
    }, 0)

    if (numberOfItems === 1) {
      return `${numberOfItems} Artikel`
    } else {
      return `${numberOfItems} Artiklar`
    }
  }

  async function onRemoveItem (cartItemId: string) {
    await removeItemFromCart(cartItemId)
  }

  async function handleChange (cartItemId: string, newQuantity: number) {
    setLoading(true)
    if (newQuantity === 0) {
      await onRemoveItem(cartItemId)
    } else {
      await updateItemQuantity({ storeId, cartItemId, quantity: newQuantity, })
    }
    if (couponApplied && coupon.code) {
      const { valid, reasons } = await validateCoupon(coupon.code)
      if (!valid) {
        setDiscountErrorMessage(getDiscountErrorMessage(reasons && reasons?.length > 0 ? reasons[0].reasonCode : ''))
      }
    }
    setLoading(false)
  }


  async function onCheckout () {
    handleCartIsOpen()
    storeId && cartItems.length > 0 && navigate(`/butik/${storeId}/stammis`)
  }


  const handleDiscount = async () => {
      setLoadingDiscount(true)
      setDiscountErrorMessage('')
      if (discountCode.length > 0) {
        try {
          const { valid, reasons } = await validateCoupon(discountCode)
          if (!valid) {
            setDiscountErrorMessage(getDiscountErrorMessage(reasons && reasons?.length > 0 ? reasons[0].reasonCode : ''))
          }
        } catch (error) {
          console.log('Error validating coupon:', error)
          setDiscountErrorMessage('Ett fel uppstod, vänligen försök igen')
        } finally {
          setLoadingDiscount(false)
        }
      } else {
        setDiscountErrorMessage('Vänligen ange en rabattkod')
        setLoadingDiscount(false)
      }
  }

  return (
    <div className={styles.cartContainer} ref={cartRef}>
      <button className={styles.cartPreview} onClick={handleCartIsOpen}>
        <p>Att betala: {getFormattedPrice(totalPrice)}</p>
        <div className={styles.numberOfArticles}>
          <p>{getNumberOfArticlesText()} </p>
          <Angle color={'#fff'} className={cartIsOpen ? styles.chevronDown : styles.chevronUp} direction="down" />
        </div>
      </button>
      <div className={cartIsOpen ? styles.cart : styles.hiddenCart}>
        <h3 className={styles.heading}>Din beställning  {getDeliveryTypeText()}</h3>
        <div className={styles.content}>
          <div className={styles.cartItems}>
            {cartItems && cartItems.length > 0 ? (
              <ul>
                {cartItems.map((item, index) => (
                  <div key={`${item.id}-${index}`}>
                    <li className={styles.cartItem} >
                      <div className={styles.itemRow}>
                        <div className={styles.cartItemInfo}>
                          <span className={styles.productName}>{item.name}</span>
                        </div>
                        {item.customizationMenuItems && item.customizationMenuItems.length > 0 && item.customizationMenuItems.map((ingredient, index) => {
                          if (!ingredient.preSelected || ingredient.price !== 0)
                            return (
                              <div key={`${ingredient.id}-${index}`} className={styles.extraIngredientRow}>
                                <span className={styles.extraIngredient}>{ingredient.customizationType === 1 ? '+' : ingredient.customizationType === 2 ? '-' : ''} {ingredient.name}</span>
                              </div>
                            )
                        }
                        )}
                      </div>
                      <div className={styles.itemQuantityAndPrice}>
                        <QuantityButton quantity={item.quantity} onDecrease={() => handleChange(item.id, item.quantity - 1)} onIncrease={() => handleChange(item.id, item.quantity + 1)} type='cart' loading={loading} />
                        <p className={styles.itemTotalPrice}>{getFormattedPrice(item.totalPrice)}</p>
                      </div>
                      <div className={styles.divider}></div>
                    </li>
                  </div>
                ))}
              </ul>
            ) : (
              <div className={styles.emptyCart}>
                <p className={styles.emptyCartText}>Din beställning kommer synas här</p>
              </div>
            )}
          </div>
          <div className={styles.checkout}>
            <div>
              <span className={styles.discountLabel}>Rabattkod</span>
              <div className={styles.discountInputContainer}> 
                <input 
                  className={discountErrorMessage ? styles.discountError : couponApplied ? styles.discountValid : styles.discountInput} 
                  value={discountCode} 
                  onChange={(e) => { setDiscountErrorMessage(''); setDiscountCode(e.target.value)}} 
                  placeholder='Ange rabattkod' 
                />
                <button 
                  className={styles.discountButton}
                   onClick={() => !couponApplied ? handleDiscount() : cartId && removeCoupon(cartId)}
                  >
                   {loadingDiscount ? 'Laddar...' : couponApplied ? 'Ta bort' : 'Registrera'}
                </button>
              </div>
                {discountErrorMessage && <p className={styles.discountErrorMessage}>{discountErrorMessage}</p>}
            </div>
            <div className={styles.totalArticles}>
              <span>{getNumberOfArticlesText()}</span>
            </div>
            <div className={styles.total}>
              <div className={styles.totalPrice}> 
                {couponApplied && couponLoading ?
                  <div className={styles.loading}>
                    <img src={loader} style={{ maxHeight: '1.5rem' }} alt="laddningsikon" />
                    Beräknar summa att betala
                  </div> :
                  <>
                    <span>Att betala</span>
                    <span>{getFormattedPrice(totalPrice)}</span>
                  </>
                }
              </div>
              {deliveryFee > 0 && (
                <div className={styles.deliveryFee}>Leveranskostnad: {getFormattedPrice(deliveryFee)}</div>
              )}
               {couponApplied &&
                    <div className={styles.totalDiscount}>
                      <span>{getDiscountTypeText(coupon)}</span>
                      <span>{getDiscountAmountText(coupon)}</span>
                    </div>
                  }
            </div>
            <button disabled={status === 'FETCHING'} onClick={onCheckout} className={styles.checkoutButton} type="button">
              {status === 'FETCHING' ? (
                <>Laddar…</>
              ) : (
                <>Betala</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function mapState (state: RootState) {
  return {
    status: state.order.status,
    cartItems: state.cart.items,
    deliveryType: state.cart.deliveryType,
    totalPrice: state.cart.totalPrice,
    tableNumber: state.cart.tableNumber,
    coupon: state.cart.coupon,
    couponLoading: state.cart.couponLoading,
    couponApplied: state.cart.couponApplied,
    currentOrderId: state.order.currentOrderId,
    openingHours: state.store.current?.openingHours,
    cartId: state.cart.cartId,
    deliveryFee: state.cart.deliveryFee,
  };
}

function mapDispatch (dispatch: Dispatch) {
  return {
    fetchCart: dispatch.cart.fetchCart,
    removeItemFromCart: dispatch.cart.removeItemFromCart,
    validateCoupon: dispatch.cart.validateCoupon,
    updateItemQuantity: dispatch.cart.updateItemQuantity,
    removeCoupon: dispatch.cart.removeCoupon,
    resetOrderStatus: dispatch.order.stopFetching,
  };
}


export default connect(mapState, mapDispatch)(Cart)